import { Alert, Anchor, Badge, Box, Button, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
  IconCircleDot,
  IconPencil,
  IconTool,
} from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { casIzMs, denar, kolicina, stevilo } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { IzbiraOsebe } from '../vozila/IzbiraOsebe';
import { opisVozila, STATUSI, type StatusNaloga, type VoziloStranke } from './delavnica';
import { opisGum, profil, SEZONE, STANJA_GUM } from './gume';
import { PoljaVozila } from './PoljaVozila';

type Oseba = Shema['OsebaPodatki'];

/**
 * Vozilo stranke (modul Delavnica): podatki in lastnik (»Uredi«), kontakt lastnika in servisna knjiga – vsi nalogi
 * vozila s postavkami, najnovejši najprej, in gume v hotelu za gume. »Sprejem vozila« odpre sprejem s tem vozilom,
 * »Shrani gume« nov komplet gum za to vozilo. Pot …/novo doda vozilo.
 */
export function PodrobnostiVozilaStranke() {
  const params = useParams();
  const novo = params.id === 'novo';
  const id = novo ? undefined : Number(params.id);
  const poizvedba = useQuery({
    queryKey: ['vozilo-stranke', id],
    enabled: !novo,
    queryFn: ({ signal }) => podatki(api.GET('/api/delavnica/vozila/{id}', { signal, params: { path: { id: id! } } })),
  });
  if (!novo && !poizvedba.data) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Vozila ni bilo mogoče naložiti"
      />
    );
  }
  return (
    <Vozilo
      key={`${params.id}-${poizvedba.dataUpdatedAt}`}
      novo={novo}
      podrobnosti={novo ? { vozilo: {}, servisi: [], gume: [] } : poizvedba.data!}
    />
  );
}

function Vozilo({ novo, podrobnosti }: { novo: boolean; podrobnosti: Shema['VoziloStrankePodrobnosti'] }) {
  const navigate = useNavigate();
  const odjemalec = useQueryClient();
  const shranjeno = podrobnosti.vozilo ?? {};
  const [urejanje, setUrejanje] = useState(novo);
  const [vozilo, setVozilo] = useState<VoziloStranke>(shranjeno);
  const [lastnik, setLastnik] = useState<Oseba | null>(
    shranjeno.kupecId ? ({ id: shranjeno.kupecId, ime: shranjeno.kupec ?? '' } as Oseba) : null,
  );
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);

  const shrani = async () => {
    setPolja({});
    setNapaka(null);
    setShranjujem(true);
    try {
      const body = { ...vozilo, kupecId: lastnik?.id ?? undefined };
      const v = novo
        ? await podatki(api.POST('/api/delavnica/vozila', { body }))
        : await podatki(api.PUT('/api/delavnica/vozila/{id}', { params: { path: { id: shranjeno.id! } }, body }));
      await odjemalec.invalidateQueries({ queryKey: ['vozila-strank'] });
      await odjemalec.invalidateQueries({ queryKey: ['vozilo-stranke', v.id] });
      notifications.show({ color: 'green', message: novo ? 'Vozilo je dodano.' : 'Spremembe so shranjene.' });
      if (novo) {
        void navigate(`/delavnica/vozila/${v.id}`, { replace: true });
      } else {
        setUrejanje(false);
      }
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setShranjujem(false);
    }
  };

  const servisi = podrobnosti.servisi ?? [];
  const gume = podrobnosti.gume ?? [];
  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/delavnica/vozila"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Vozila strank
      </Button>
      <GlavaZaslona
        naslov={novo ? 'Novo vozilo' : opisVozila(shranjeno) || 'Vozilo'}
        opis={novo ? undefined : shranjeno.kupec}
        pomoc="vozila-strank"
        dejanja={
          urejanje ? (
            <Group gap="xs">
              <Button
                variant="subtle"
                onClick={() => {
                  if (novo) {
                    void navigate('/delavnica/vozila');
                    return;
                  }
                  setVozilo(shranjeno);
                  setPolja({});
                  setUrejanje(false);
                }}
                disabled={shranjujem}
              >
                Prekliči
              </Button>
              <Button onClick={() => void shrani()} loading={shranjujem}>
                {novo ? 'Dodaj' : 'Shrani'}
              </Button>
            </Group>
          ) : (
            <Group gap="xs">
              <Button
                component={Link}
                to={`/delavnica/sprejem?vozilo=${shranjeno.id}`}
                leftSection={<IconTool size={18} />}
              >
                Sprejem vozila
              </Button>
              <Button variant="default" leftSection={<IconPencil size={18} />} onClick={() => setUrejanje(true)}>
                Uredi
              </Button>
            </Group>
          )
        }
      />
      {napaka && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} mb="md" role="alert">
          {napaka}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Kartica naslov="Podatki vozila">
          {urejanje ? (
            <PoljaVozila vozilo={vozilo} nastavi={setVozilo} polja={polja} />
          ) : (
            <Podatki
              vrstice={[
                ['Registrska', shranjeno.registrska],
                ['VIN', shranjeno.vin],
                ['Vozilo', [shranjeno.znamka, shranjeno.model].filter(Boolean).join(' ')],
                ['Letnik', shranjeno.letnik],
                ['Kilometri', shranjeno.km != null ? stevilo(shranjeno.km) : ''],
                ['Motor', [shranjeno.motor, shranjeno.gorivo].filter(Boolean).join(' · ')],
                ['Opomba', shranjeno.opomba],
              ]}
            />
          )}
        </Kartica>
        <Kartica naslov="Lastnik">
          {urejanje ? (
            <IzbiraOsebe
              vrsta="kupci"
              oznaka="Lastnik"
              obvezno={false}
              izbran={lastnik}
              izberi={setLastnik}
              napaka={polja.kupecId}
            />
          ) : (
            <Podatki
              vrstice={[
                [
                  'Ime',
                  shranjeno.kupecId ? (
                    <Anchor component={Link} to={`/osebe/kupci/${shranjeno.kupecId}`}>
                      {shranjeno.kupec}
                    </Anchor>
                  ) : (
                    ''
                  ),
                ],
                [
                  'Telefon',
                  podrobnosti.telefon ? <Anchor href={`tel:${podrobnosti.telefon}`}>{podrobnosti.telefon}</Anchor> : '',
                ],
                [
                  'E-pošta',
                  podrobnosti.email ? <Anchor href={`mailto:${podrobnosti.email}`}>{podrobnosti.email}</Anchor> : '',
                ],
              ]}
            />
          )}
        </Kartica>
      </SimpleGrid>
      {!novo && (
        <Box mt="md">
          <Kartica
            naslov="Hotel za gume"
            dejanja={
              <Button
                component={Link}
                to={`/delavnica/gume/nov?vozilo=${shranjeno.id}`}
                variant="light"
                size="compact-md"
                leftSection={<IconCircleDot size={16} />}
              >
                Shrani gume
              </Button>
            }
          >
            {gume.length === 0 ? (
              <Text size="sm" c="dimmed">
                Gume tega vozila niso v hotelu.
              </Text>
            ) : (
              <Stack gap="xs">
                {gume.map((g) => (
                  <Group key={g.id} justify="space-between" gap="xs" wrap="wrap">
                    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Anchor component={Link} to={`/delavnica/gume/${g.id}`} fw={700} className="brez-preloma">
                        {g.lokacija || '–'}
                      </Anchor>
                      {g.sezona && (
                        <Badge color={SEZONE[g.sezona].barva} variant="light">
                          {SEZONE[g.sezona].naziv}
                        </Badge>
                      )}
                      <Text size="sm" truncate>
                        {opisGum(g)}
                      </Text>
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      {g.obrabljene && (
                        <IconAlertTriangle size={16} color="var(--mantine-color-red-6)" aria-label="Obrabljene" />
                      )}
                      <Text size="sm" c="dimmed" className="brez-preloma">
                        {profil(g.minProfil)} · {g.stanje ? STANJA_GUM[g.stanje] : ''}
                      </Text>
                    </Group>
                  </Group>
                ))}
              </Stack>
            )}
          </Kartica>
        </Box>
      )}
      {!novo && (
        <Box mt="md">
          <Kartica naslov="Servisna knjiga">
            {servisi.length === 0 ? (
              <Text size="sm" c="dimmed">
                Vozilo še ni bilo v delavnici.
              </Text>
            ) : (
              <Stack gap="sm">
                {servisi.map((s) => (
                  <Paper key={s.nalogId} withBorder radius="md" p="sm">
                    <Group justify="space-between" gap="xs" wrap="wrap">
                      <Group gap="xs">
                        <Anchor component={Link} to={`/delavnica/nalogi/${s.nalogId}`} fw={700}>
                          {s.stevilka}
                        </Anchor>
                        <Text size="sm" c="dimmed">
                          {casIzMs(s.sprejeto)}
                          {s.km != null ? ` · ${stevilo(s.km)} km` : ''}
                        </Text>
                        {s.status && (
                          <Badge color={STATUSI[s.status as StatusNaloga].barva} variant="light">
                            {STATUSI[s.status as StatusNaloga].naziv}
                          </Badge>
                        )}
                      </Group>
                      <Text fw={600}>{denar(s.skupaj)} €</Text>
                    </Group>
                    {s.tezava && (
                      <Text size="sm" mt={4} style={{ whiteSpace: 'pre-wrap' }}>
                        {s.tezava}
                      </Text>
                    )}
                    {(s.postavke ?? []).length > 0 && (
                      <Stack gap={0} mt={4}>
                        {(s.postavke ?? []).map((p) => (
                          <Text key={p.id} size="sm" c="dimmed">
                            {kolicina(p.kolicina)} {p.enota} × {p.naziv}
                          </Text>
                        ))}
                      </Stack>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </Kartica>
        </Box>
      )}
    </ZaslonSeznama>
  );
}
